/**
 * Seeks the player makes itself (the audio/video switch) must not be logged
 * as a listener "scroll". The suppression covers the FIRST `seeked` after a
 * mark, however late it lands (a fresh audio source seeks once its metadata
 * arrives, a restarted HLS load on a slow network can take seconds). A
 * re-mark before it lands (the iOS re-apply) still covers one `seeked`, as
 * the browser coalesces the two seeks. A seek that never lands stops
 * suppressing after PROGRAMMATIC_SEEK_TIMEOUT_MS, so a later viewer seek is
 * not swallowed.
 */
const suppressedUntil = new WeakMap()

export const PROGRAMMATIC_SEEK_TIMEOUT_MS = 10_000

export function markProgrammaticSeek(el, now = Date.now()) {
    suppressedUntil.set(el, now + PROGRAMMATIC_SEEK_TIMEOUT_MS)
}

export function isProgrammaticSeek(el, now = Date.now()) {
    return (suppressedUntil.get(el) ?? 0) > now
}

/**
 * For the `seeked` handler: true when this seek is the programmatic one,
 * and consumes the mark so the next seek counts as the viewer's again.
 */
export function consumeProgrammaticSeek(el, now = Date.now()) {
    const programmatic = isProgrammaticSeek(el, now)
    suppressedUntil.delete(el)

    return programmatic
}

/**
 * A programmatic seek made before the metadata is known lands later (iOS
 * drops the first currentTime set). Until it lands, the element's intended
 * position is that target while currentTime still reads 0 (the dropped
 * seek), so a listener that runs before the re-apply (the deferred audio
 * play beacon, a quick switch back) still reads the right second. A non-zero
 * currentTime is a real position (playback or a viewer seek) and wins.
 */
const pendingTargets = new WeakMap()

export function setPendingSeek(el, seconds) {
    pendingTargets.set(el, seconds)
}

export function clearPendingSeek(el) {
    pendingTargets.delete(el)
}

export function intendedPosition(el) {
    const pending = pendingTargets.get(el)

    return pending !== undefined && !el.currentTime ? pending : el.currentTime || 0
}
